import { useEffect, useState } from 'react';
import type { AdminKPIData } from '@flymind/shared';
import { useAdmin } from '../../hooks/useAdmin';
import { useTranslation } from '../../hooks/useTranslation';
import { AdminPageHeader, AdminStatCard } from '../../components/admin';
import { Spinner } from '../../components/ui/Spinner';

export default function AdminDashboard() {
  const { adminApi } = useAdmin();
  const { t, locale } = useTranslation();
  const numberLocale = locale === 'en' ? 'en-US' : 'it-IT';
  const [data, setData] = useState<AdminKPIData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchKPIs() {
      try {
        setLoading(true);
        setError(null);
        const res = await adminApi.get<AdminKPIData>(
          '/dashboard/kpis',
        );
        if (!cancelled) {
          setData(res);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : t('admin.common.loadingError'),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchKPIs();
    return () => {
      cancelled = true;
    };
  }, [adminApi, t]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <AdminPageHeader title={t('admin.dashboard.title')} />
        <div className="rounded-lg border border-red-300 bg-red-50 p-6 text-center">
          <p className="text-sm font-medium text-red-800">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 text-sm font-medium text-red-600 underline hover:text-red-800"
          >
            {t('admin.common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const maxPlanCount = Math.max(...data.subscriptionsByPlan.map((s) => s.count), 1);

  const planLabels: Record<string, string> = {
    free: t('admin.common.planFree'),
    premium: t('admin.common.planPremium'),
    unlimited: t('admin.common.planUnlimited'),
  };

  const planColors: Record<string, string> = {
    free: 'bg-gray-400',
    premium: 'bg-primary-500',
    unlimited: 'bg-primary-700',
  };

  return (
    <div>
      <AdminPageHeader
        title={t('admin.dashboard.title')}
        subtitle={t('admin.dashboard.subtitle')}
      />

      {/* KPI Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <AdminStatCard
          label={t('admin.dashboard.totalUsers')}
          value={data.totalUsers.toLocaleString(numberLocale)}
          icon={
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
          }
        />
        <AdminStatCard
          label={t('admin.dashboard.activeUsers')}
          value={data.activeUsers.toLocaleString(numberLocale)}
          icon={
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          }
        />
        <AdminStatCard
          label={t('admin.dashboard.activeMonitors')}
          value={data.activeMonitors.toLocaleString(numberLocale)}
          icon={
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
              <path
                fillRule="evenodd"
                d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                clipRule="evenodd"
              />
            </svg>
          }
        />
        <AdminStatCard
          label={t('admin.dashboard.mrr')}
          value={`${data.mrr.toLocaleString(numberLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} \u20ac`}
          icon={
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
              <path
                fillRule="evenodd"
                d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                clipRule="evenodd"
              />
            </svg>
          }
        />
        <AdminStatCard
          label={t('admin.dashboard.conversionRate')}
          value={`${(data.conversionRate * 100).toFixed(1)}%`}
          icon={
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
                clipRule="evenodd"
              />
            </svg>
          }
        />
        <AdminStatCard
          label={t('admin.dashboard.newUsersWeek')}
          value={data.newUsersThisWeek.toLocaleString(numberLocale)}
          icon={
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
            </svg>
          }
        />
      </div>

      {/* Subscriptions by plan */}
      <section className="mt-8">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">
          {t('admin.dashboard.subscriptionsByPlan')}
        </h2>
        <div className="rounded-lg border border-border bg-surface p-5">
          {data.subscriptionsByPlan.length === 0 ? (
            <p className="text-sm text-text-secondary">
              {t('admin.dashboard.noActiveSubscription')}
            </p>
          ) : (
            <div className="space-y-4">
              {data.subscriptionsByPlan.map((item) => (
                <div key={item.plan}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="font-medium text-text-primary">
                      {planLabels[item.plan] ?? item.plan}
                    </span>
                    <span className="text-text-secondary">
                      {item.count.toLocaleString(numberLocale)}
                    </span>
                  </div>
                  <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                      className={`h-full rounded-full transition-all ${planColors[item.plan] ?? 'bg-primary-500'}`}
                      style={{
                        width: `${(item.count / maxPlanCount) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* New users summary */}
      <section className="mt-8">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">
          {t('admin.dashboard.newUsers')}
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-surface p-5">
            <p className="text-sm font-medium text-text-secondary">
              {t('admin.dashboard.thisWeek')}
            </p>
            <p className="mt-1 text-3xl font-bold text-text-primary">
              {data.newUsersThisWeek.toLocaleString(numberLocale)}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-surface p-5">
            <p className="text-sm font-medium text-text-secondary">
              {t('admin.dashboard.thisMonth')}
            </p>
            <p className="mt-1 text-3xl font-bold text-text-primary">
              {data.newUsersThisMonth.toLocaleString(numberLocale)}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
